import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectNotifications, selectUser } from '../../../store/general/general-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { defaultAvailableSubscriptionSets } from '../../../models/general/notifications/notification-trigger';
import GeneralLoaderWrapper from '@common/components/General/GeneralLoaderWrapper';
import { NotificationGroupType, NotificationSendMethod } from '../../../models/enums/notifications';
import NotificationList from './NotificationList';
import { resetNotificationRestState } from '../../../store/general/general-slice';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DevicePushNotificationsEnabled, EmailIcon } from '@common/utils/icons/icons';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';

const NotificationConfiguration: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const notificationData = useAppSelector((state) => selectNotifications(state));
  const userData = useAppSelector((state) => selectUser(state));

  const resetNotificationsRestStateHandler = () => {
    dispatch(
      resetNotificationRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        patchId: null,
      }),
    );
  };

  const notificationSet = defaultAvailableSubscriptionSets.map((set) => {
    const existingNotification = notificationData.data?.find((n) => n.trigger === set.trigger);
    return {
      ...set,
      repeat: existingNotification?.repeat ?? set.repeat,
      id: existingNotification?.id,
    };
  });

  return (
    <React.Fragment>
      <GeneralLoaderWrapper
        getStatus={notificationData.rest.getStatus}
        dataLoaded={notificationData.data != null}
        failedToLoadMessage={
          notificationData.rest.getError?.message ?? t('error-loading-notification-subscriptions')
        }
      >
        <CustomSplitColumnCard
          title={t('title-notifications')}
          content={[
            {
              left: (
                <Stack spacing={2} py={1} px={2}>
                  <Stack direction="row" alignItems="center" spacing={1} py={1}>
                    <EmailIcon />
                    <Typography>{t('title-email-notifications')}</Typography>
                  </Stack>
                  <CustomAlert
                    header={`${t('info-email-notifications')} ${userData.data.email}`}
                    severity="info"
                  />
                </Stack>
              ),
              right: (
                <React.Fragment>
                  {Object.values(NotificationGroupType)
                    .filter((groupType) =>
                      notificationSet.some(
                        (defaultSet) =>
                          (defaultSet.sendMethod === NotificationSendMethod.EMAIL ||
                            defaultSet.sendMethod === undefined) &&
                          defaultSet.type === groupType,
                      ),
                    )
                    .map((groupType) => (
                      <NotificationList
                        key={groupType}
                        sendMethod={NotificationSendMethod.EMAIL}
                        groupType={groupType}
                        subscriptions={notificationData.data ?? []}
                        optionSet={notificationSet.filter((option) => option.type === groupType)}
                      />
                    ))}
                </React.Fragment>
              ),
            },
            {
              left: (
                <Stack spacing={2} py={1} px={2}>
                  <Stack direction="row" alignItems="center" spacing={1} py={1}>
                    <DevicePushNotificationsEnabled />
                    <Typography>{t('title-push-notifications')}</Typography>
                  </Stack>
                  <CustomAlert header={t('info-push-notifications')} severity="info" />
                </Stack>
              ),
              right: (
                <React.Fragment>
                  {Object.values(NotificationGroupType)
                    .filter((groupType) =>
                      notificationSet.some(
                        (defaultSet) =>
                          (defaultSet.sendMethod === NotificationSendMethod.PUSH ||
                            defaultSet.sendMethod === undefined) &&
                          defaultSet.type === groupType,
                      ),
                    )
                    .map((groupType) => (
                      <NotificationList
                        key={groupType}
                        groupType={groupType}
                        sendMethod={NotificationSendMethod.PUSH}
                        subscriptions={notificationData.data ?? []}
                        optionSet={notificationSet.filter((option) => option.type === groupType)}
                      />
                    ))}
                </React.Fragment>
              ),
            },
          ]}
        />
      </GeneralLoaderWrapper>
      <CustomSnackbar
        open={notificationData.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-notification')}
        onClose={resetNotificationsRestStateHandler}
      />
      <CustomSnackbar
        open={notificationData.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-patching-notification')}
        onClose={resetNotificationsRestStateHandler}
      />
      <CustomSnackbar
        open={notificationData.rest.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting-notification')}
        onClose={resetNotificationsRestStateHandler}
      />
    </React.Fragment>
  );
};

export default NotificationConfiguration;
